import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Base wizard steps. When the resolved school has ask_teacher: true (see
// schools.yaml), a 'teacher' step is inserted right after 'grade' — teacher
// choices are filtered by the already-selected grade, so grade must come
// first. See buildSteps().
const BASE_STEPS = [
  { key: 'bread', title: 'Choose Your Bread', required: true },
  { key: 'cheese', title: 'Choose Your Cheese', required: true },
  { key: 'meat', title: 'Choose Your Meat', required: true },
  { key: 'condiments', title: 'Choose Your Condiments (Optional)', required: false, multiple: true },
  { key: 'toppings', title: 'Choose Your Toppings (Optional)', required: false, multiple: true },
  { key: 'cookie', title: 'Choose Your Cookie', required: true },
  { key: 'side', title: 'Choose Your Side', required: true },
  { key: 'grade', title: 'Choose Your Grade', required: true },
  { key: 'student', title: 'Enter Name', required: true },
  { key: 'email', title: 'Enter Email (Optional)', required: false },
  { key: 'payment', title: 'Choose Payment Method', required: true },
  { key: 'summary', title: 'Review Your Order', required: true }
];

const TEACHER_STEP = { key: 'teacher', title: "Choose Your Teacher", required: true };

// Deliberately permissive (not RFC 5322-complete) — this only exists to
// catch obvious typos before submit, not to be an exhaustive validator.
// The email step itself stays optional (see BASE_STEPS/canProceed): a blank
// value is always fine, only a non-blank value gets checked against this.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (value) => EMAIL_PATTERN.test(value.trim());

// Sentinel selected when someone picks "my teacher isn't listed" instead of
// one of the actual teacher names. Never sent to the backend as-is — see
// handleOtherTeacherChange / submitOrder, which send the typed name plus
// is_other_teacher: true instead.
const OTHER_TEACHER = '__other__';

// Public page header: school name (falls back to a generic label before
// options have loaded or if none is configured) plus the school's logo, if
// one has been uploaded (Schools.js admin form) — see
// models.School.LogoImage / SchoolInfo.LogoDataURI on the backend. Shown
// large, above the title, so it reads as prominent and trustworthy right at
// the top of the page.
function SchoolHeader({ school }) {
  return (
    <header className="header no-print">
      <div className="container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
        {school?.logo_data_uri && (
          <img
            src={school.logo_data_uri}
            alt={`${school.name || 'School'} logo`}
            style={{
              height: '3.5rem',
              maxWidth: '90%',
              width: 'auto',
              objectFit: 'contain',
              borderRadius: '6px',
              backgroundColor: 'white',
              padding: '0.35rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}
          />
        )}
        <h1 style={{ margin: 0, textAlign: 'center' }}>
          {school?.name ? `${school.name} Lunchday` : 'Lunchday'}
        </h1>
      </div>
    </header>
  );
}

// Footer link to the site's marketing landing page (backend/middleware's
// landingPage — see AGENTS.md's "Schools are database-backed" section).
// Served at "/about" on any host, including a school's own subdomain, so
// this is just a same-site relative link — no need to compute a base
// domain here at all, and it works the same in local dev
// ("localhost:3000/about") as production.
function AboutUsFooter() {
  return (
    <footer className="no-print" style={{ textAlign: 'center', padding: '2rem 1rem', color: '#888', fontSize: '0.9rem' }}>
      <a href="/about" style={{ color: 'inherit' }}>About Us</a>
    </footer>
  );
}

function buildSteps(askTeacher) {
  if (!askTeacher) return BASE_STEPS;
  const gradeIndex = BASE_STEPS.findIndex(s => s.key === 'grade');
  return [
    ...BASE_STEPS.slice(0, gradeIndex + 1),
    TEACHER_STEP,
    ...BASE_STEPS.slice(gradeIndex + 1)
  ];
}

function SandwichWizard() {
  const [currentStep, setCurrentStep] = useState(0);
  const [options, setOptions] = useState(null);
  const [selections, setSelections] = useState({
    bread: '',
    cheese: '',
    meat: '',
    condiments: [],
    toppings: [],
    cookie: '',
    side: '',
    grade: '',
    teacher: '',
    otherTeacherName: '',
    studentName: '',
    email: '',
    payment: ''
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [nextOrderDate, setNextOrderDate] = useState(null);
  const [upcomingOrderDates, setUpcomingOrderDates] = useState([]);
  const [showLanding, setShowLanding] = useState(true);
  const [teachers, setTeachers] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const optionsResponse = await axios.get('/api/public/options');
      const optionsData = optionsResponse.data;

      // Grade options, payment options, and school-specific settings
      // (name, price, Venmo handle) all come from the backend, resolved
      // from the request's domain. See schools.yaml.
      setOptions(optionsData);

      // Only schools with ask_teacher: true need the teacher list
      if (optionsData.school?.ask_teacher) {
        try {
          const teachersResponse = await axios.get('/api/public/teachers');
          setTeachers(teachersResponse.data);
        } catch (teachersError) {
          console.error('Error fetching teachers:', teachersError);
          setTeachers([]);
        }
      }

      // Try to get next order date
      try {
        const orderDateResponse = await axios.get('/api/public/next-order-date');
        setNextOrderDate(orderDateResponse.data);
      } catch (orderDateError) {
        // No order date available
        setNextOrderDate(null);
      }

      // Always fetch upcoming dates for FAQ section
      try {
        const upcomingResponse = await axios.get('/api/public/upcoming-order-dates');
        setUpcomingOrderDates(upcomingResponse.data);
      } catch (upcomingError) {
        console.error('Error fetching upcoming order dates:', upcomingError);
        setUpcomingOrderDates([]);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching options:', error);
      setLoading(false);
    }
  };

  // Keeps the browser tab title in sync with whichever school the backend
  // resolved (same /api/public/options call as everything else on this
  // page) — runs identically in dev and prod, unlike the server-rendered
  // <title>/meta tags in public/index.html (see main.go's renderIndexHTML),
  // which only production's Go binary can produce since CRA's dev server
  // serves that file directly without running any Go code. Those
  // server-side tags remain the only thing crawlers/link-preview bots ever
  // see (they don't run JS), so this effect is purely for the live tab.
  useEffect(() => {
    if (options?.school?.name) {
      document.title = `${options.school.name} Lunchday - School Lunch Orders`;
    }
  }, [options]);

  // Recomputed whenever options load/change; adds the 'teacher' step only
  // for schools with ask_teacher: true (see schools.yaml / buildSteps()).
  const STEPS = buildSteps(options?.school?.ask_teacher);

  // Whether the 'teacher' step has anything to show for the given grade. If
  // not, the wizard skips straight past it in both directions rather than
  // asking a question with no valid answers.
  const teacherStepHasOptions = (grade) => teachers.some(t => t.grade === grade);

  // Given a step index we're about to land on, returns the index to
  // actually land on — skipping the 'teacher' step if it would have no
  // options for the currently-selected grade.
  const skipEmptyTeacherStep = (index, direction) => {
    if (STEPS[index]?.key === 'teacher' && !teacherStepHasOptions(selections.grade)) {
      return index + direction;
    }
    return index;
  };

  const handleSelection = (stepKey, value) => {
    const step = STEPS[currentStep];

    if (step.multiple) {
      setSelections(prev => {
        const currentValues = prev[stepKey] || [];
        const newValues = currentValues.includes(value)
          ? currentValues.filter(v => v !== value)
          : [...currentValues, value];
        return { ...prev, [stepKey]: newValues };
      });
    } else {
      setSelections(prev => {
        // Changing grade invalidates any previously-selected teacher, since
        // teacher choices are filtered by grade.
        const clearTeacher = stepKey === 'grade' && value !== prev.grade;
        return {
          ...prev,
          [stepKey]: value,
          ...(clearTeacher ? { teacher: '', otherTeacherName: '' } : {})
        };
      });

      // Selecting "Other" needs a typed-in name before we can move on, so
      // don't auto-advance — the text field's own Next button handles it.
      if (stepKey === 'teacher' && value === OTHER_TEACHER) {
        return;
      }

      // Auto-advance to next step for single-choice questions
      setTimeout(() => {
        if (currentStep < STEPS.length - 1) {
          let next = currentStep + 1;
          // If we just picked a grade with no teachers listed, skip the
          // teacher step entirely rather than asking an unanswerable question.
          if (stepKey === 'grade' && STEPS[next]?.key === 'teacher' && !teachers.some(t => t.grade === value)) {
            next += 1;
          }
          setCurrentStep(next);
        }
      }, 300); // Small delay for visual feedback
    }
  };

  const handleStudentNameChange = (value) => {
    setSelections(prev => ({ ...prev, studentName: value }));
  };

  const handleEmailChange = (value) => {
    setSelections(prev => ({ ...prev, email: value }));
  };

  const handleOtherTeacherNameChange = (value) => {
    setSelections(prev => ({ ...prev, otherTeacherName: value }));
  };

  // Returns from the "Other" text field back to the teacher picker grid,
  // without auto-advancing (unlike handleSelection, which is for actually
  // answering a step).
  const backToTeacherList = () => {
    setSelections(prev => ({ ...prev, teacher: '', otherTeacherName: '' }));
  };

  const canProceed = () => {
    const step = STEPS[currentStep];

    if (step.key === 'student') {
      return selections.studentName.trim() !== '';
    }

    if (step.key === 'teacher' && selections.teacher === OTHER_TEACHER) {
      return selections.otherTeacherName.trim() !== '';
    }

    if (step.key === 'email') {
      // Optional, so blank is fine — but if something was typed, it must
      // look like a real address before we let them move on.
      return selections.email.trim() === '' || isValidEmail(selections.email);
    }

    if (step.key === 'summary') {
      return true; // Summary can always proceed to submit
    }

    if (step.required) {
      const value = selections[step.key];
      return step.multiple ? (value && value.length > 0) : (value !== '');
    }

    return true; // Optional steps can always be proceeded
  };

  const nextStep = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(skipEmptyTeacherStep(currentStep + 1, 1));
    } else {
      submitOrder();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(skipEmptyTeacherStep(currentStep - 1, -1));
    }
  };

  const submitOrder = async () => {
    setSubmitting(true);

    try {
      const isOtherTeacher = selections.teacher === OTHER_TEACHER;
      const orderData = {
        student_name: selections.studentName,
        student_grade: selections.grade,
        teacher: isOtherTeacher ? selections.otherTeacherName.trim() : selections.teacher,
        is_other_teacher: isOtherTeacher,
        email: selections.email,
        payment: selections.payment,
        bread: selections.bread,
        cheese: selections.cheese,
        meat: selections.meat,
        condiments: selections.condiments,
        toppings: selections.toppings,
        cookie: selections.cookie,
        side: selections.side
      };

      const response = await axios.post('/api/public/orders', orderData);
      setOrderResult(response.data);
      setOrderComplete(true);
    } catch (error) {
      console.error('Error submitting order:', error);
      alert('Failed to submit order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetWizard = () => {
    setCurrentStep(0);
    setSelections({
      bread: '',
      cheese: '',
      meat: '',
      condiments: [],
      toppings: [],
      cookie: '',
      side: '',
      grade: '',
      teacher: '',
      otherTeacherName: '',
      studentName: '',
      email: '',
      payment: ''
    });
    setOrderComplete(false);
    setOrderResult(null);
  };

  const formatOrderDate = (dateString) => {
    if (!dateString) return 'Lunchday';

    // Parse the date string properly to avoid timezone conversion issues
    // If the date string is in YYYY-MM-DD format, parse it as local date
    if (dateString.includes('T')) {
      // Full ISO string - use as is
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      });
    } else {
      // Date-only string - parse as local date to avoid UTC conversion
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-indexed
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  if (loading) {
    return (
      <>
        <SchoolHeader school={null} />
        <div>Loading options...</div>
      </>
    );
  }

  const school = options?.school || {};
  const priceStr = (school.price_usd ?? 0).toFixed(2);
  const venmoNote = (ref, name) => `Reference%20%23${ref}%20-%20${encodeURIComponent(name)}`;
  const venmoAppLink = (ref, name) =>
    `venmo://paycharge?txn=pay&audience=private&amount=${priceStr}&note=${venmoNote(ref, name)}&recipients=${school.venmo_username}`;
  const venmoWebLink = (ref, name) =>
    `https://account.venmo.com/pay?audience=private&amount=${priceStr}&note=${venmoNote(ref, name)}&recipients=%2C${school.venmo_username}&txn=pay`;

  // Show message when no order dates are available
  if (!loading && !nextOrderDate) {
    return (
      <>
        <SchoolHeader school={school} />
        <div className="orders-list">
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '2px solid #e9ecef'
        }}>
          <h3 style={{ color: '#6c757d', marginBottom: '1rem' }}>
            Orders are not open at this time
          </h3>
          {upcomingOrderDates.length > 0 ? (
            <div>
              <p style={{ color: '#6c757d', fontSize: '1.1rem', marginBottom: '2rem' }}>
                Here are the upcoming lunch order dates:
              </p>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                {upcomingOrderDates.map(orderDate => (
                  <div key={orderDate.id} style={{
                    backgroundColor: 'white',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    padding: '1.5rem',
                    textAlign: 'left'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.9rem' }}>
                      <div>
                        <strong style={{ color: '#007bff' }}>Lunch Date:</strong><br />
                        {formatOrderDate(orderDate.delivery_date)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p style={{ color: '#6c757d', fontSize: '0.9rem', marginTop: '2rem' }}>
              There are currently no lunch dates available for ordering. Check back closer to the lunch date, or see below.<br />
              Please contact <a href={`mailto:${school.contact_email}`}>{school.contact_email}</a> for questions.
              </p>
            </div>
          ) : (
            <p style={{ color: '#6c757d', fontSize: '1.1rem', lineHeight: '1.6' }}>
              There are currently no upcoming lunch order dates available.<br />
              Please contact <a href={`mailto:${school.contact_email}`}>{school.contact_email}</a> for questions.
            </p>
          )}
        </div>
        </div>
        <AboutUsFooter />
      </>
    );
  }

  if (orderComplete && orderResult) {
    return (
      <>
        <SchoolHeader school={school} />
        <div className="orders-list">
        <h2>✅ Order Submitted Successfully!</h2>
        <div className="order-card">
          <div className="order-header">
            <div>
              <strong>Reference: {orderResult.reference_number}</strong>
              <div>Name: {orderResult.name}</div>
              <div>Grade: {orderResult.grade}</div>
              {orderResult.teacher && (
                <div>Teacher: {orderResult.teacher}</div>
              )}
              {orderResult.email && (
                <div>Email: {orderResult.email}</div>
              )}
            </div>
            <div className="order-status status-pending">
              {orderResult.status}
            </div>
          </div>

          <div>
            <h4>Your Sandwich:</h4>
            <ul>
              <li><strong>Bread:</strong> {orderResult.bread}</li>
              <li><strong>Cheese:</strong> {orderResult.cheese}</li>
              <li><strong>Meat:</strong> {orderResult.meat}</li>
              {orderResult.condiments && <li><strong>Condiments:</strong> {orderResult.condiments}</li>}
              {orderResult.toppings && <li><strong>Toppings:</strong> {orderResult.toppings}</li>}
              <li><strong>Cookie:</strong> {orderResult.cookie}</li>
              <li><strong>Side:</strong> {orderResult.side}</li>
              <li><strong>Payment:</strong> {orderResult.payment}</li>
            </ul>
          </div>

          {/* Payment Instructions */}
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '1.5rem',
            borderRadius: '8px',
            margin: '1rem 0',
            border: '2px solid #e9ecef'
          }}>
            <h4 style={{marginTop: 0, color: '#2c3e50'}}>💰 Payment Instructions</h4>

            {orderResult.payment === 'Cash/Check' ? (
              <div>
                <p style={{fontSize: '1.1rem', marginBottom: '1rem'}}>
                  <strong>Please deliver your payment to the school office:</strong>
                </p>
                <ul style={{paddingLeft: '1.5rem'}}>
                  <li>Payment method: Cash or Check</li>
                  <li>Please use a labeled envelope for cash, including student name</li>
                  <li>Refer to Reference #{orderResult.reference_number}</li>
                  <li>Name: {orderResult.name}</li>
                  <li>Location: Main School Office</li>
                </ul>
                <p style={{
                  fontSize: '0.9rem',
                  color: '#666',
                  fontStyle: 'italic',
                  marginTop: '1rem'
                }}>
                </p>
              </div>
            ) : orderResult.payment === 'Venmo' ? (
              <div>
                <p style={{fontSize: '1.1rem', marginBottom: '1.5rem'}}>
                  <strong>Complete your payment via Venmo (${priceStr}):</strong>
                </p>

                {/* Mobile Payment Options */}
                <div style={{
                  backgroundColor: '#ffffff',
                  border: '2px solid #28a745',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem'
                }}>
                  <h5 style={{margin: '0 0 0.75rem 0', color: '#28a745'}}>📱 Pay on Mobile Device</h5>

                  {/* Mobile App Link */}
                  <div style={{marginBottom: '1rem'}}>
                    <p style={{fontSize: '0.9rem', color: '#666', marginBottom: '0.75rem'}}>
                      <strong>Option 1:</strong> Tap to open Venmo mobile app:
                    </p>
                    <div style={{textAlign: 'center'}}>
                      <a
                        href={venmoAppLink(orderResult.reference_number, orderResult.name)}
                        style={{
                          display: 'inline-block',
                          backgroundColor: '#28a745',
                          color: 'white',
                          padding: '10px 20px',
                          borderRadius: '8px',
                          textDecoration: 'none',
                          fontSize: '1rem',
                          fontWeight: 'bold',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      >
                        📲 Open Venmo App
                      </a>
                    </div>
                  </div>

                  {/* QR Code Option */}
                  <div>
                    <p style={{fontSize: '0.9rem', color: '#666', marginBottom: '0.75rem'}}>
                      <strong>Option 2:</strong> Scan QR code with your phone's camera app:
                    </p>
                    <div style={{textAlign: 'center'}}>
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(venmoAppLink(orderResult.reference_number, orderResult.name))}`}
                        alt="Venmo Payment QR Code"
                        style={{
                          border: '2px solid #ddd',
                          borderRadius: '8px',
                          padding: '10px',
                          backgroundColor: 'white'
                        }}
                      />
                      <p style={{fontSize: '0.8rem', color: '#999', marginTop: '0.5rem'}}>
                        Use your phone's camera app (not the Venmo app)
                      </p>
                    </div>
                  </div>

                  {/* Manual entry Option */}
                  <div>
                    <p style={{fontSize: '0.9rem', color: '#666', marginBottom: '0.75rem'}}>
                      <strong>Option 3:</strong> Manual payment
                    </p>
                    <div style={{textAlign: 'center'}}>
                      <p style={{fontSize: '0.8rem', color: '#999', marginTop: '0.5rem'}}>
                        Open Venmo and enter the following details:
                      </p>
                      <p style={{fontSize: '0.8rem', color: '#999', marginTop: '0.5rem'}}>
                        <strong>Amount:</strong> ${priceStr}
                      </p>
                      <p style={{fontSize: '0.8rem', color: '#999', marginTop: '0.5rem'}}>
                        <strong>Note:</strong> Reference #{orderResult.reference_number} - {orderResult.name}
                      </p>
                      <p style={{fontSize: '0.8rem', color: '#999', marginTop: '0.5rem'}}>
                        <strong>Recipient:</strong> {school.venmo_username}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Computer Payment Option */}
                <div style={{
                  backgroundColor: '#ffffff',
                  border: '2px solid #3D95CE',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem'
                }}>
                  <h5 style={{margin: '0 0 0.75rem 0', color: '#3D95CE'}}>💻 Pay on Computer/Web Browser</h5>
                  <p style={{fontSize: '0.9rem', color: '#666', marginBottom: '1rem'}}>
                    Click the button below to open Venmo in your web browser:
                  </p>
                  <div style={{textAlign: 'center'}}>
                    <a
                      href={venmoWebLink(orderResult.reference_number, orderResult.name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-block',
                        backgroundColor: '#3D95CE',
                        color: 'white',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      💳 Open Venmo Web
                    </a>
                  </div>
                </div>

                <div style={{fontSize: '0.9rem', color: '#666', textAlign: 'center'}}>
                  <p><strong>Payment Note:</strong> Reference #{orderResult.reference_number}</p>
                </div>
              </div>
            ) : null}
          </div>

          <button
            className="button success"
            onClick={resetWizard}
            style={{marginTop: '1rem'}}
          >
            Order Another Sandwich
          </button>
        </div>
        </div>
        <AboutUsFooter />
      </>
    );
  }

  const step = STEPS[currentStep];
  const stepOptions = options[step.key];
  // Teachers are filtered to the grade already selected, since a K teacher
  // shouldn't show up as a choice for a 3rd grader and vice versa.
  const teacherOptions = teachers.filter(t => t.grade === selections.grade).map(t => t.name);
  // What to actually display for the teacher selection — the typed name
  // when "Other" was chosen, otherwise the picked value as-is.
  const displayTeacherName = selections.teacher === OTHER_TEACHER
    ? selections.otherTeacherName
    : selections.teacher;
  const isOtherTeacherEntry = step.key === 'teacher' && selections.teacher === OTHER_TEACHER;

  // Only flag as an error once something's actually been typed — an empty
  // field is valid (the step is optional), so no red border on first view.
  const emailHasError = selections.email.trim() !== '' && !isValidEmail(selections.email);

  // Show landing page first
  if (showLanding) {
    return (
      <>
        <SchoolHeader school={school} />
        <div className="orders-list">
        <div style={{
          backgroundColor: '#f8f9fa',
          border: '2px solid #e9ecef',
          borderRadius: '8px',
          padding: '2rem',
          marginBottom: '2rem',
          lineHeight: '1.6'
        }}>
          <h3 style={{ color: '#495057', marginBottom: '1rem' }}>
            Order Your {school.name} Subway Sandwich Online!
          </h3>

          <p style={{ marginBottom: '1.5rem', fontSize: '1.1rem' }}>
            All students are invited to <strong>pre-order</strong> a custom-made Subway meal to be delivered at lunchtime!
            Use this digital ordering system to place and pay for your order quickly and easily.
          </p>

          {nextOrderDate && (
            <div style={{
              backgroundColor: '#d4edda',
              border: '1px solid #c3e6cb',
              borderRadius: '4px',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <strong>📅 Order Deadline: </strong>
              {formatOrderDate(nextOrderDate.close_date)}
              <br />
              <strong>🏫 School Lunch Date: </strong>
              {formatOrderDate(nextOrderDate.delivery_date)}
              <br />
              <strong>💰 Cost: ${priceStr}</strong>
            </div>
          )}

          A portion of the proceeds from each purchased sandwich goes to the school!

          <h4 style={{ color: '#495057', marginBottom: '0.75rem' }}>What's Included:</h4>
          <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
            <li>Six-inch custom sandwich</li>
            <li>One cookie of your choice</li>
            <li>One side (applesauce, yogurt, or chips)</li>
            <li style={{ color: '#dc3545', fontWeight: 'bold' }}>
              Drink NOT included
            </li>
          </ul>

          <div style={{ textAlign: 'center' }}>
            <button
              onClick={() => setShowLanding(false)}
              style={{
                padding: '1rem 2rem',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#218838'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#28a745'}
            >
              Start My Order
            </button>
          </div>

          <h4 style={{ color: '#495057', marginBottom: '0.75rem' }}>Important Information:</h4>
          <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem', fontSize: '0.95rem' }}>
            <li><strong>Late orders will NOT be accepted</strong> - order by the deadline</li>
            <li><strong>Payment required</strong> - orders without payment will be rejected</li>
            <li><strong>No refunds</strong> - once ordered and paid, refunds cannot be issued</li>
            <li><strong>Absent students</strong> - meals cannot be saved for the next day</li>
            <li><strong>No obligation</strong> - you can still choose regular school lunch or bring a packed lunch on order days</li>
          </ul>

          <div style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '4px',
            padding: '1rem',
            marginBottom: '2rem'
          }}>
            <strong>💡 Tip: </strong>
            You can pay with Venmo at the end of your order, or pay afterwards with cash/check at the school office.
          </div>

          <h4 style={{ color: '#495057', marginBottom: '1rem' }}>❓ Frequently Asked Questions</h4>

          <div style={{ marginBottom: '2rem' }}>
            <details style={{ marginBottom: '1rem', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <summary style={{
                padding: '0.75rem',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer',
                fontWeight: 'bold',
                borderRadius: '4px'
              }}>
                What if my child is absent on delivery day?
              </summary>
              <div style={{ padding: '1rem', fontSize: '0.95rem' }}>
                Unfortunately, meals cannot be saved for the next day. If your student is absent, the meal will not be available later.
              </div>
            </details>

            <details style={{ marginBottom: '1rem', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <summary style={{
                padding: '0.75rem',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer',
                fontWeight: 'bold',
                borderRadius: '4px'
              }}>
                Can I get a refund if plans change?
              </summary>
              <div style={{ padding: '1rem', fontSize: '0.95rem' }}>
                No refunds will be issued once an order is placed and payment is submitted, regardless of circumstances.
              </div>
            </details>

            <details style={{ marginBottom: '1rem', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <summary style={{
                padding: '0.75rem',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer',
                fontWeight: 'bold',
                borderRadius: '4px'
              }}>
                What about drinks?
              </summary>
              <div style={{ padding: '1rem', fontSize: '0.95rem' }}>
                Drinks are NOT included with Subway meals. Students can purchase milk through the lunchroom or bring a drink from home.
                If your student receives free or reduced hot lunch, he/she must also purchase milk or bring a drink from home. The hot lunch program cannot provide milk apart from a lunch as part of the free and reduced program.
              </div>
            </details>

            <details style={{ marginBottom: '1rem', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <summary style={{
                padding: '0.75rem',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer',
                fontWeight: 'bold',
                borderRadius: '4px'
              }}>
                What if I miss the order deadline?
              </summary>
              <div style={{ padding: '1rem', fontSize: '0.95rem' }}>
                Late orders will NOT be accepted. Make sure to place your order before the deadline shown above.
              </div>
            </details>

            <details style={{ marginBottom: '1rem', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <summary style={{
                padding: '0.75rem',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer',
                fontWeight: 'bold',
                borderRadius: '4px'
              }}>
                Can my child still get regular school lunch on Subway day?
              </summary>
              <div style={{ padding: '1rem', fontSize: '0.95rem' }}>
                Yes! Ordering Subway is completely optional. Your child can still choose regular school lunch or bring a packed lunch from home.
              </div>
            </details>

            <details style={{ marginBottom: '1rem', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <summary style={{
                padding: '0.75rem',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer',
                fontWeight: 'bold',
                borderRadius: '4px'
              }}>
                How do I pay with Venmo?
              </summary>
              <div style={{ padding: '1rem', fontSize: '0.95rem' }}>
                After completing your order, Venmo options will be presented, including a QR code and web payment link. You can use these to pay quickly and easily.
                In addition you'll receive a reference number. You can then pay via Venmo using this reference number,
                or you can pay later with cash/check at the school office.
              </div>
            </details>
            <details style={{ marginBottom: '1rem', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <summary style={{
                padding: '0.75rem',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer',
                fontWeight: 'bold',
                borderRadius: '4px'
              }}>
                What if I am paying for multiple orders?
              </summary>
              <div style={{ padding: '1rem', fontSize: '0.95rem' }}>
                You can pay for multiple orders by sending individual payments through Venmo at the completion of each order. There is no additional charge for multiple payments.
                Alternatively, you can send a single venmo transaction for the full amount, or using cash/check at the school office.
              </div>
            </details>
            <details style={{ marginBottom: '1rem', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <summary style={{
                padding: '0.75rem',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer',
                fontWeight: 'bold',
                borderRadius: '4px'
              }}>
                What days will Subway be available for lunch at school?
              </summary>
              <div style={{ padding: '1rem', fontSize: '0.95rem' }}>
                <ul>
                  {upcomingOrderDates.length > 0 ? (
                    upcomingOrderDates.map(orderDate => (
                      <li key={orderDate.id}>
                        {formatOrderDate(orderDate.delivery_date)}
                      </li>
                    ))
                  ) : (
                    <li>Loading upcoming dates...</li>
                  )}
                </ul>

                Orders typically open about a month before and close about a week before Subway day.
              </div>
            </details>
            <details style={{ marginBottom: '1rem', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <summary style={{
                padding: '0.75rem',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer',
                fontWeight: 'bold',
                borderRadius: '4px'
              }}>
                Do I have to order through this website?
              </summary>
              <div style={{ padding: '1rem', fontSize: '0.95rem' }}>
                No, students can still order via paper forms at school.
              </div>
            </details>
            <details style={{ marginBottom: '1rem', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <summary style={{
                padding: '0.75rem',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer',
                fontWeight: 'bold',
                borderRadius: '4px'
              }}>
                Who can I contact for other questions?
              </summary>
              <div style={{ padding: '1rem', fontSize: '0.95rem' }}>
                Please reach out to the {school.short_name} PTSO at <a href={`mailto:${school.contact_email}`}>{school.contact_email}</a>
              </div>
            </details>
          </div>
        </div>
        </div>
        <AboutUsFooter />
      </>
    );
  }

  return (
    <>
      <SchoolHeader school={school} />
      <div className="orders-list">
      {/* Home Link */}
      <div style={{ marginBottom: '0.5rem', textAlign: 'right' }}>
        <a
          href="/"
          style={{
            color: '#007bff',
            textDecoration: 'none',
            fontSize: '0.9rem',
            fontWeight: 'bold'
          }}
          onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
          onMouseOut={(e) => e.target.style.textDecoration = 'none'}
          onClick={(e) => {
            e.preventDefault();
            const confirmed = window.confirm('Are you sure you want to go back to the home page? This will clear your current progress.');
            if (confirmed) {
              window.location.href = '/';
            }
          }}
        >
          ← Start Over
        </a>
      </div>

      <h2 style={{ margin: '0 0 0.25rem 0' }}>Sandwich Order for {formatOrderDate(nextOrderDate?.delivery_date)}</h2>
      {nextOrderDate && (
        <p style={{
          textAlign: 'center',
          color: '#666',
          marginTop: 0,
          marginBottom: '1rem',
          fontSize: '1.1rem'
        }}>
          Orders close on {formatOrderDate(nextOrderDate.close_date)}
        </p>
      )}

      {/* Progress Bar */}
      <div style={{marginBottom: '1rem'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem'}}>
          <span>Step {currentStep + 1} of {STEPS.length}</span>
          <span>{Math.round((currentStep / STEPS.length) * 100)}% Complete</span>
        </div>
        <div style={{
          width: '100%',
          height: '10px',
          backgroundColor: '#ddd',
          borderRadius: '5px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${(currentStep / STEPS.length) * 100}%`,
            height: '100%',
            backgroundColor: '#3498db',
            transition: 'width 0.3s ease'
          }} />
        </div>
      </div>

      <div className="order-card">
        <h3>{step.title}</h3>
        {!step.multiple && step.key !== 'student' && step.key !== 'email' && step.key !== 'summary' && !isOtherTeacherEntry &&
          !(step.key === 'teacher' && teacherOptions.length === 0) && (
          <p style={{color: '#666', fontSize: '0.9rem', marginBottom: '1rem'}}>
            Click an option to automatically continue to the next step.
          </p>
        )}
        {step.multiple && (
          <p style={{color: '#666', fontSize: '0.9rem', marginBottom: '1rem'}}>
            Click Next after making your selections
          </p>
        )}

        {step.key === 'student' ? (
          <div className="form-group">
            <label>Student or Staff Name</label>
            <input
              type="text"
              value={selections.studentName}
              onChange={(e) => handleStudentNameChange(e.target.value)}
              placeholder="Enter the first and last name of the student or staff"
              maxLength={30}
              style={{
                fontSize: '1.1rem',
                padding: '0.75rem',
                width: '100%',
                maxWidth: '500px',
                boxSizing: 'border-box'
              }}
            />
          </div>
        ) : step.key === 'email' ? (
          <div className="form-group">
            <label>Email Address (Optional)</label>
            <input
              type="email"
              value={selections.email}
              onChange={(e) => handleEmailChange(e.target.value)}
              placeholder="Enter email address for order confirmation (optional)"
              style={{
                fontSize: '1.1rem',
                padding: '0.75rem',
                width: '100%',
                maxWidth: '500px',
                boxSizing: 'border-box',
                border: emailHasError ? '1px solid #dc3545' : undefined
              }}
            />
            {emailHasError ? (
              <p style={{fontSize: '0.9rem', color: '#dc3545', marginTop: '0.5rem'}}>
                That doesn't look like a valid email address.
              </p>
            ) : (
              <p style={{fontSize: '0.9rem', color: '#666', marginTop: '0.5rem'}}>
                Email is optional but will allow us to send you a confirmation
              </p>
            )}
          </div>
        ) : isOtherTeacherEntry ? (
          <div className="form-group">
            <label>Teacher's Name</label>
            <input
              type="text"
              value={selections.otherTeacherName}
              onChange={(e) => handleOtherTeacherNameChange(e.target.value)}
              placeholder="Enter your teacher's name"
              autoFocus
              maxLength={50}
              style={{
                fontSize: '1.1rem',
                padding: '0.75rem',
                width: '100%',
                maxWidth: '500px',
                boxSizing: 'border-box'
              }}
            />
            <p style={{fontSize: '0.9rem', color: '#666', marginTop: '0.5rem'}}>
              <button
                type="button"
                onClick={backToTeacherList}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#007bff',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: '0.9rem',
                  textDecoration: 'underline'
                }}
              >
                ← Choose from the list instead
              </button>
            </p>
          </div>
        ) : step.key === 'teacher' && teacherOptions.length === 0 ? (
          <p style={{color: '#666'}}>
            No teachers are listed for {selections.grade} grade yet. Please contact{' '}
            <a href={`mailto:${school.contact_email}`}>{school.contact_email}</a> for help,
            or go back and double-check your grade selection.
          </p>
        ) : step.key === 'summary' ? (
          <div className="summary-container">
            {/* Big Submit Button */}
            <div style={{textAlign: 'center', marginBottom: '2rem'}}>
              <button
                onClick={submitOrder}
                disabled={submitting}
                style={{
                  backgroundColor: submitting ? '#6c757d' : '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '16px 48px',
                  borderRadius: '8px',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                  minWidth: '200px'
                }}
              >
                {submitting ? 'Submitting...' : 'Submit Order'}
              </button>
            </div>

            <div style={{backgroundColor: '#f8f9fa', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem'}}>
              <h4 style={{marginTop: 0, color: '#2c3e50'}}>Order Summary</h4>

              <div style={{display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))'}}>
                <div className="summary-section">
                  <h5 style={{color: '#34495e', borderBottom: '2px solid #3498db', paddingBottom: '0.5rem'}}>Personal Information</h5>
                  <p><strong>Name:</strong> {selections.studentName}</p>
                  <p><strong>Grade:</strong> {selections.grade}</p>
                  {displayTeacherName && (
                    <p><strong>Teacher:</strong> {displayTeacherName}</p>
                  )}
                  {selections.email && (
                    <p><strong>Email:</strong> {selections.email}</p>
                  )}
                </div>

                <div className="summary-section">
                  <h5 style={{color: '#34495e', borderBottom: '2px solid #3498db', paddingBottom: '0.5rem'}}>Sandwich Details</h5>
                  <p><strong>Bread:</strong> {selections.bread}</p>
                  <p><strong>Cheese:</strong> {selections.cheese}</p>
                  <p><strong>Meat:</strong> {selections.meat}</p>
                  {selections.condiments && selections.condiments.length > 0 && (
                    <p><strong>Condiments:</strong> {selections.condiments.join(', ')}</p>
                  )}
                  {selections.toppings && selections.toppings.length > 0 && (
                    <p><strong>Toppings:</strong> {selections.toppings.join(', ')}</p>
                  )}
                </div>

                <div className="summary-section">
                  <h5 style={{color: '#34495e', borderBottom: '2px solid #3498db', paddingBottom: '0.5rem'}}>Sides & Extras</h5>
                  <p><strong>Cookie:</strong> {selections.cookie}</p>
                  <p><strong>Side:</strong> {selections.side}</p>
                  <p><strong>Payment:</strong> {selections.payment}</p>
                </div>
              </div>
            </div>

            <div style={{textAlign: 'center', color: '#7f8c8d', fontSize: '0.9rem'}}>
              Please review your order above and click "Submit Order" when ready.
            </div>
          </div>
        ) : (
          <div className="menu-grid" style={{gridTemplateColumns: step.multiple ? 'repeat(auto-fit, minmax(200px, 1fr))' : 'repeat(auto-fit, minmax(250px, 1fr))'}}>
            {(step.key === 'teacher' ? teacherOptions : stepOptions)?.map((option, index) => {
              const isSelected = step.multiple
                ? selections[step.key]?.includes(option)
                : selections[step.key] === option;

              return (
                <div
                  key={index}
                  className={`menu-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleSelection(step.key, option)}
                  style={{
                    cursor: 'pointer',
                    border: isSelected ? '3px solid #3498db' : '1px solid #ddd',
                    backgroundColor: isSelected ? '#e3f2fd' : 'white',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <h4 style={{margin: '0 0 0.5rem 0', textAlign: 'center'}}>
                    {step.multiple && isSelected && '✓ '}{option}
                  </h4>
                  {step.multiple && (
                    <p style={{fontSize: '0.8rem', color: '#666', textAlign: 'center'}}>
                      Click to {isSelected ? 'remove' : 'add'}
                    </p>
                  )}
                </div>
              );
            })}

            {/* "My teacher isn't listed" — lets someone type in a name not
                in the admin's list, e.g. a new hire or a substitute. */}
            {step.key === 'teacher' && (
              <div
                className="menu-item"
                onClick={() => handleSelection('teacher', OTHER_TEACHER)}
                style={{
                  cursor: 'pointer',
                  border: '1px dashed #999',
                  backgroundColor: 'white',
                  transition: 'all 0.2s ease'
                }}
              >
                <h4 style={{margin: '0 0 0.5rem 0', textAlign: 'center', color: '#666'}}>
                  Other (not listed)
                </h4>
              </div>
            )}
          </div>
        )}

        {step.multiple && (
          <div style={{marginTop: '1rem', fontSize: '0.9rem', color: '#666'}}>
            Selected: {selections[step.key]?.length || 0} item(s)
            {selections[step.key]?.length > 0 && (
              <div style={{marginTop: '0.5rem'}}>
                <strong>Current selections:</strong> {selections[step.key].join(', ')}
              </div>
            )}
          </div>
        )}

        <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '2rem'}}>
          <button
            className="button"
            onClick={prevStep}
            disabled={currentStep === 0}
          >
            Previous
          </button>

          {/* Show Next button for multiple-choice questions, student name step, email step, and the "Other" teacher text field */}
          {(step.multiple || step.key === 'student' || step.key === 'email' || isOtherTeacherEntry) && (
            <button
              className="button success"
              onClick={nextStep}
              disabled={!canProceed() || submitting}
            >
              Next
            </button>
          )}

          {/* Show instruction for single-choice questions */}
          {!step.multiple && step.key !== 'student' && step.key !== 'email' && step.key !== 'summary' && !isOtherTeacherEntry &&
            !(step.key === 'teacher' && teacherOptions.length === 0) && (
            <div style={{
              color: '#666',
              fontSize: '0.9rem',
              alignSelf: 'center',
              fontStyle: 'italic'
            }}>
              Select an option to continue automatically
            </div>
          )}
        </div>
      </div>
      </div>
      <AboutUsFooter />
    </>
  );
}

export default SandwichWizard;
